<script setup>
import { computed, onMounted, ref } from "vue";
import { onClickOutside } from "@/utils/onClickOutside.js";
import { useLanguageStore } from "@/stores/languageStore.js";

const props = defineProps({
  variant: {
    type: String,
    default: "pill"
  }
});

const languageStore = useLanguageStore();
const open = ref(false);
const root = ref(null);

const currentOption = computed(() =>
  languageStore.options.find(option => option.code === languageStore.language)
  || languageStore.options[0]
);

onClickOutside(root, () => { open.value = false; });

onMounted(async () => {
  try {
    await languageStore.initialize();
  } catch {
    // Session may not exist yet.
  }
});


async function choose(code) {
  try {
    await languageStore.setLanguage(
      code
    );
  } catch (error) {
    console.warn(
      "Backend language update failed.",
      error
    );
  } finally {
    open.value = false;
  }
}
</script>

<template>
  <div
    ref="root"
    class="language-menu"
    :class="{ 'home-tile-variant': props.variant === 'home-tile' }"
  >
    <button
      class="language-trigger"
      :class="{ 'home-action-tile': props.variant === 'home-tile' }"
      type="button"
      @click="open = !open"
      :aria-expanded="open"
      aria-haspopup="listbox"
    >
      <template v-if="props.variant === 'home-tile'">
        <span class="globe-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" />
            <ellipse cx="12" cy="12" rx="4.2" ry="9" />
            <path d="M3 12h18" />
          </svg>
        </span>
        <span class="home-language-label">{{ currentOption.shortLabel }}</span>
        <span class="dropdown-caret" aria-hidden="true"></span>
      </template>
      <template v-else>
        <span aria-hidden="true">🌐</span>
        <span>{{ currentOption.shortLabel }}</span>
      </template>
    </button>

    <div v-if="open" class="language-popup" role="listbox">
      <button
        v-for="option in languageStore.options"
        :key="option.code"
        type="button"
        class="language-option"
        :class="{ active: option.code === languageStore.language }"
        role="option"
        :aria-selected="option.code === languageStore.language"
        @click="choose(option.code)"
      >
        <span>{{ option.label }}</span>
        <span v-if="option.code === languageStore.language">✓</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.language-menu {
  position: relative;
  z-index: 20;
}

.language-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(82,246,232,.45);
  border-radius: 999px;
  padding: 9px 13px;
  color: #fff;
  background: rgba(4,29,46,.86);
}

.home-tile-variant {
  width: 104px;
  height: 64px;
}

.language-trigger.home-action-tile {
  position: relative;
  width: 104px;
  height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 7px 6px;
  border: 1.2px solid rgba(82,246,232,.82);
  border-radius: 18px;
  color: #fff;
  background: rgba(4,28,48,.88);
  box-shadow: 0 0 14px rgba(82,246,232,.18), inset 0 0 0 1px rgba(255,255,255,.025);
  backdrop-filter: blur(8px);
}

.language-trigger.home-action-tile:hover,
.language-trigger.home-action-tile:focus-visible {
  border-color: rgba(255,255,255,.92);
  box-shadow: 0 0 18px rgba(82,246,232,.26);
  outline: none;
}

.globe-icon {
  display: inline-flex;
  width: 24px;
  height: 24px;
}

.globe-icon svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: #fff;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.home-language-label {
  width: 100%;
  overflow: hidden;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.05;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-caret {
  position: absolute;
  right: 9px;
  bottom: 7px;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid rgba(255,255,255,.90);
}

.language-popup {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 170px;
  padding: 7px;
  border: 1px solid rgba(82,246,232,.52);
  border-radius: 14px;
  background: rgba(3,17,31,.98);
  box-shadow: 0 18px 45px rgba(0,0,0,.45), 0 0 18px rgba(82,246,232,.12);
}

.home-tile-variant .language-popup {
  top: auto;
  right: 0;
  bottom: calc(100% + 8px);
}

.language-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 0;
  border-radius: 9px;
  padding: 10px 11px;
  color: #fff;
  background: transparent;
  text-align: left;
}

.language-option:hover,
.language-option.active {
  color: #83fff4;
  background: rgba(82,246,232,.12);
}

/* LANGUAGE_POPUP_SCROLL_V1_START */

/* 语言较多时，只让语言列表内部滚动 */
.language-popup {
  max-height: min(70dvh, 520px);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* LANGUAGE_POPUP_SCROLL_V1_END */

/* LANGUAGE_TRIGGER_FRAMELESS_V1_START */

/*
  只删除主页右上角语言切换按钮的蓝色框。
  不修改点击功能，也不删除语言下拉列表。
*/
.language-trigger,
.language-trigger:hover,
.language-trigger:focus-visible,
.language-trigger.home-action-tile,
.language-trigger.home-action-tile:hover,
.language-trigger.home-action-tile:focus-visible {
  border: 0;
  outline: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
}

/* LANGUAGE_TRIGGER_FRAMELESS_V1_END */
</style>
